import { useCallback, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'

import {
  NAV_SECTIONS,
  groupContainsActive,
  type NavGroup,
  type NavItem,
} from '@/components/layout/nav-config'
import { useRailTooltip } from '@/components/ui/RailTooltip'
import { cn } from '@/lib/utils'

export interface SidebarNavProps {
  /** Icon-only rail. Labels move into hover/focus tooltips. */
  collapsed: boolean
  /** Called after any navigation — the drawer closes itself on it. */
  onNavigate?: (() => void) | undefined
}

const GROUP_STORAGE_PREFIX = 'cbs.nav.'

function readGroupState(id: string, fallback: boolean): boolean {
  try {
    const stored = localStorage.getItem(`${GROUP_STORAGE_PREFIX}${id}`)
    return stored === null ? fallback : stored === 'true'
  } catch {
    // Private mode / disabled storage: the default is a fine answer.
    return fallback
  }
}

function writeGroupState(id: string, expanded: boolean): void {
  try {
    localStorage.setItem(`${GROUP_STORAGE_PREFIX}${id}`, String(expanded))
  } catch {
    // Losing the preference is not worth breaking navigation over.
  }
}

/**
 * The label beside the icon, or — on the rail — the name a screen reader reads.
 *
 * The VISIBLE rail label is the portalled tooltip owned by `NavItemLink`; it
 * cannot live here, because a child of the scrolling sidebar gets clipped at its
 * edge.
 */
function NavLabel({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (!collapsed) return <span className="min-w-0 flex-1 truncate">{label}</span>
  return <span className="sr-only">{label}</span>
}

interface NavItemLinkProps {
  item: NavItem
  collapsed: boolean
  /** Children of an expanded group get an indent and a smaller icon. */
  nested?: boolean | undefined
  onNavigate?: (() => void) | undefined
}

function NavItemLink({ item, collapsed, nested = false, onNavigate }: NavItemLinkProps) {
  const Icon = item.icon
  const color = item.color
  const { anchorProps, tooltip } = useRailTooltip(item.label, collapsed)

  return (
    <NavLink
      to={item.path}
      end={item.end ?? false}
      onClick={onNavigate}
      {...anchorProps}
      // aria-current is what a screen reader announces; the tint is the sighted
      // equivalent. The active row is washed with the item's own colour.
      style={({ isActive }) => (isActive && color ? { backgroundColor: `${color}22` } : undefined)}
      className={({ isActive }) =>
        cn(
          'flex h-10 items-center gap-3 rounded-control text-sm transition-colors',
          collapsed ? 'w-10 justify-center px-0' : 'w-full px-3',
          !collapsed && nested && 'pl-11',
          isActive
            ? 'font-medium text-fg'
            : 'text-fg-secondary hover:bg-sidebar-hover hover:text-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Icons carry their own colour always, so the rail reads at a glance;
              inactive ones sit a touch back. */}
          <Icon
            className={cn(
              'size-4 shrink-0',
              !color && (isActive ? 'text-accent' : 'text-fg-muted'),
            )}
            style={color ? { color, opacity: isActive ? 1 : 0.85 } : undefined}
            aria-hidden
          />
          <NavLabel label={item.label} collapsed={collapsed} />
          {tooltip}
        </>
      )}
    </NavLink>
  )
}

interface NavGroupItemProps {
  group: NavGroup
  collapsed: boolean
  onNavigate?: (() => void) | undefined
}

function NavGroupItem({ group, collapsed, onNavigate }: NavGroupItemProps) {
  const { pathname } = useLocation()
  const containsActive = groupContainsActive(group, pathname)
  const [expanded, setExpanded] = useState(() => readGroupState(group.id, containsActive))

  // Landing on a child route (from the palette, a `g` jump, or a deep link)
  // must reveal where you are, without overwriting the stored preference.
  useEffect(() => {
    if (containsActive) setExpanded(true)
  }, [containsActive])

  const toggle = useCallback(() => {
    setExpanded((wasExpanded) => {
      const next = !wasExpanded
      writeGroupState(group.id, next)
      return next
    })
  }, [group.id])

  const Icon = group.icon
  const panelId = `nav-group-${group.id}`

  // The rail has no room for a sub-list, so the group's children are promoted
  // to plain icons — every destination stays one click away.
  if (collapsed) {
    return (
      <li>
        <ul className="flex flex-col items-center gap-1">
          {group.children.map((child) => (
            <li key={child.path}>
              <NavItemLink item={child} collapsed onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      </li>
    )
  }

  return (
    <li>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={cn(
          'flex h-10 w-full items-center gap-3 rounded-control px-3 text-sm transition-colors hover:bg-sidebar-hover',
          containsActive ? 'font-medium text-fg' : 'text-fg-secondary hover:text-fg',
        )}
      >
        <Icon
          className={cn('size-4 shrink-0', containsActive ? 'text-accent' : 'text-fg-muted')}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-fg-muted transition-transform',
            expanded && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {expanded && (
        <ul id={panelId} className="mt-1 flex flex-col gap-1">
          {group.children.map((child) => (
            <li key={child.path}>
              <NavItemLink item={child} collapsed={false} nested onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * The nav tree rendered from `nav-config`.
 *
 * Separators sit between *groups* of destinations, not between every row: the
 * grouping is the information, and a rule per item would only be noise.
 */
export function SidebarNav({ collapsed, onNavigate }: SidebarNavProps) {
  return (
    <nav aria-label="Main" className="flex flex-col gap-1 px-3">
      {NAV_SECTIONS.map((section) => (
        <ul
          key={section.id}
          className={cn(
            'flex flex-col gap-1',
            collapsed && 'items-center',
            section.separated && 'mt-2 border-t border-border pt-2',
          )}
        >
          {section.entries.map((entry) =>
            entry.kind === 'group' ? (
              <NavGroupItem
                key={entry.id}
                group={entry}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ) : (
              <li key={entry.path}>
                <NavItemLink item={entry} collapsed={collapsed} onNavigate={onNavigate} />
              </li>
            ),
          )}
        </ul>
      ))}
    </nav>
  )
}
